// src/pages/markets/board/state/mutedTopics.ts
//
// A user's muted tags and categories, merged into a column's filters on the way
// to the wire and never written back into saved state — unmuting has to restore
// the column, and a preference baked into the saved filters would not.

import type { BoardFilters, BoardSchema } from '../api/boardTypes';

export interface MutedTopics {
    tags: string[];
    categories: string[];
}

export const EMPTY_MUTED: MutedTopics = { tags: [], categories: [] };

function stringArray(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value.filter((v): v is string => typeof v === 'string');
}

// The stored document, read defensively: a preference written by an older build
// must degrade to "nothing muted" rather than to a board that shows nothing.
export function normalizeMuted(data: unknown): MutedTopics {
    if (!data || typeof data !== 'object') return EMPTY_MUTED;
    const doc = data as Record<string, unknown>;
    return { tags: stringArray(doc.tags), categories: stringArray(doc.categories) };
}

// The backend validates both params against a fixed vocabulary and answers 400
// on anything else, so a value the schema no longer offers is dropped here
// rather than blanking every column. No schema yet means nothing is muted:
// widening for one render beats a request that cannot succeed.
export function knownMuted(muted: MutedTopics, schema: BoardSchema | undefined): MutedTopics {
    if (!schema) return EMPTY_MUTED;
    // Guarded like every other schema accessor: this runs in the global header,
    // where a throw white-screens the app rather than just the board.
    const knownTags = schema.polymarket_tags ?? [];
    const knownCategories = schema.event_categories ?? [];
    const tags = muted.tags.filter(t => knownTags.includes(t));
    const categories = muted.categories.filter(c => knownCategories.includes(c));
    if (tags.length === muted.tags.length && categories.length === muted.categories.length) {
        return muted;
    }
    return { tags, categories };
}

function merge(current: string[] | null, muted: string[]): string[] {
    const out = [...(current ?? [])];
    for (const value of muted) {
        if (!out.includes(value)) out.push(value);
    }
    return out;
}

// Exclude only. The backend applies include AND NOT exclude, so a muted value
// left in the include list resolves that column to nothing — which is what
// muting means. Stripping it would drop the include filter and widen the
// column to everything instead.
export function applyMuted(filters: BoardFilters, muted: MutedTopics): BoardFilters {
    if (muted.tags.length === 0 && muted.categories.length === 0) return filters;
    const next = { ...filters };
    if (muted.tags.length > 0) {
        next.tagsExclude = merge(filters.tagsExclude, muted.tags);
    }
    if (muted.categories.length > 0) {
        next.eventsExclude = merge(filters.eventsExclude, muted.categories);
    }
    return next;
}
